import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@clouway/ui'
import { Plus, Trash2 } from 'lucide-react'

import { isMigratedRoleId } from '@/api/access'
import { roleDisplayName } from '@/features/users'
import type { CatalogRole } from '@/features/users'
import {
  useAssignServiceAccountRole,
  useCatalogProducts,
  useCatalogRoles,
  useRevokeServiceAccountRole,
  useServiceAccountAccessPreview,
  useServiceAccountRoleAssignments,
} from '../hooks'

// The subject-keyed sibling of the users feature's AccessTab, pointed at the
// service account's own role-assignment/access-preview endpoints. Same
// product-select / assign / revoke / preview shape — kept as a sibling
// component (rather than a shared one parameterised over "subject") since the
// two subjects hit entirely different resource paths, and only two of the
// nine i18n strings actually differ (see the `serviceAccount.access.*`
// overrides below the shared `access.*` copy).
export function ServiceAccountAccessTab({ serviceAccountId, canWrite }: { serviceAccountId: string; canWrite: boolean }) {
  const { t, i18n } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { data: products, isLoading: productsLoading } = useCatalogProducts()
  const [product, setProduct] = useState('')

  useEffect(() => {
    if (!product && products && products.length > 0) {
      setProduct(products[0])
    }
  }, [product, products])

  const { data: assignments, isLoading: assignmentsLoading } = useServiceAccountRoleAssignments(serviceAccountId)
  const { data: catalogRoles } = useCatalogRoles(product)
  const { data: access, isLoading: accessLoading } = useServiceAccountAccessPreview(serviceAccountId, product)
  const { mutateAsync: assign } = useAssignServiceAccountRole(serviceAccountId)
  const { mutate: revoke } = useRevokeServiceAccountRole(serviceAccountId)

  if (productsLoading) {
    return <Skeleton className="h-48 w-full" />
  }

  if (!products || products.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('access.product.empty')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    )
  }

  const assignedForProduct = (assignments ?? []).filter((assignment) => assignment.product === product)
  const assignedRoleIds = new Set(assignedForProduct.map((assignment) => assignment.roleId))
  const assignableRoles = (catalogRoles ?? []).filter(
    (role) => !assignedRoleIds.has(role.id) && !isMigratedRoleId(role.id),
  )

  return (
    <div className="flex flex-col gap-4">
      {products.length > 1 && (
        <Select value={product} onValueChange={setProduct}>
          <SelectTrigger className="w-64" aria-label={t('access.product.label')}>
            <SelectValue placeholder={t('access.product.label')} />
          </SelectTrigger>
          <SelectContent>
            {products.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-sm">{t('access.assigned.title')}</CardTitle>
            {canWrite && (
              <AssignRoleDialog
                product={product}
                roles={assignableRoles}
                onAssign={(roleId) => assign({ product, roleId })}
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {assignmentsLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : assignedForProduct.length === 0 ? (
            <span className="text-muted-foreground text-sm">{t('access.assigned.empty')}</span>
          ) : (
            assignedForProduct.map((assignment) => {
              const catalogRole = catalogRoles?.find((role) => role.id === assignment.roleId)
              const migrated = isMigratedRoleId(assignment.roleId)
              const label = catalogRole ? roleDisplayName(catalogRole, i18n.language) : assignment.roleId
              return (
                <div key={assignment.roleId} className="flex items-center justify-between gap-2 rounded-md border p-2">
                  <div>
                    <div className="text-sm font-medium">{label}</div>
                    <div className="text-muted-foreground text-xs">{assignment.roleId}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {migrated ? (
                      <Badge variant="outline" className="text-muted-foreground">
                        {t('access.badge.migrated')}
                      </Badge>
                    ) : catalogRole ? (
                      <Badge variant={catalogRole.custom ? 'default' : 'secondary'}>
                        {t(catalogRole.custom ? 'access.badge.custom' : 'access.badge.preset')}
                      </Badge>
                    ) : null}
                    {canWrite && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" aria-label={t('access.assigned.revoke')}>
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('access.assigned.revoke')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('serviceAccount.access.assigned.revokeDescription', { role: label })}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => revoke({ product, roleId: assignment.roleId })}>
                              {tCommon('ui.button.confirm')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('access.preview.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {accessLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : !access ||
            (access.scopes.length === 0 && access.roles.length === 0 && access.permissions.length === 0) ? (
            <span className="text-muted-foreground text-sm">{t('access.preview.empty')}</span>
          ) : (
            <>
              <PreviewGroup label={t('access.preview.roles')} values={access.roles} />
              <PreviewGroup label={t('access.preview.scopes')} values={access.scopes} />
              {access.permissions.length > 0 && (
                <PreviewGroup label={t('access.preview.permissions')} values={access.permissions} />
              )}
            </>
          )}
        </CardContent>
        <CardFooter>
          <span className="text-muted-foreground text-xs">{t('serviceAccount.access.preview.caption')}</span>
        </CardFooter>
      </Card>
    </div>
  )
}

function PreviewGroup({ label, values }: { label: string; values: string[] }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div className="flex flex-wrap gap-1">
        {values.map((value) => (
          <Badge key={value} variant="secondary">
            {value}
          </Badge>
        ))}
      </div>
    </div>
  )
}

function AssignRoleDialog({
  product,
  roles,
  onAssign,
}: {
  product: string
  roles: CatalogRole[]
  onAssign: (roleId: string) => Promise<void>
}) {
  const { t, i18n } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [roleId, setRoleId] = useState('')

  const submit = async () => {
    if (!roleId) return
    await onAssign(roleId)
    setRoleId('')
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={roles.length === 0}>
          <Plus className="size-4" />
          {t('access.assign.trigger')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('access.assign.title')}</DialogTitle>
          <DialogDescription>{t('access.assign.description', { product })}</DialogDescription>
        </DialogHeader>
        {roles.length === 0 ? (
          <span className="text-muted-foreground text-sm">{t('access.assign.empty')}</span>
        ) : (
          <Select value={roleId} onValueChange={setRoleId}>
            <SelectTrigger aria-label={t('access.assign.placeholder')}>
              <SelectValue placeholder={t('access.assign.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {roles.map((role) => (
                <SelectItem key={role.id} value={role.id}>
                  {roleDisplayName(role, i18n.language)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {tCommon('ui.button.cancel')}
          </Button>
          <Button onClick={submit} disabled={!roleId}>
            {tCommon('ui.button.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
